import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaWallet, FaSyncAlt, FaSearch, FaClock, FaImages, FaReceipt } from 'react-icons/fa';
import { getWallet } from '../services/api';
import WalletWithdrawals from './WalletWithdrawals';
import './Wallet.css';

const STATUSES = { completed: 'Terminé', pending: 'En attente', failed: 'Échoué', cancelled: 'Annulé', refunded: 'Remboursé' };
const amount = value => `${Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} F CFA`;

const Wallet = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const updateBalance = useCallback(summary => setData(current => current ? { ...current, wallet: summary } : current), []);

  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const response = await getWallet({ status, search, page });
        if (active) setData(response);
      } catch (err) {
        if (active) setError(err.message || 'Impossible de charger votre portefeuille.');
      } finally {
        if (active) setLoading(false);
      }
    }, search ? 300 : 0);
    return () => { active = false; clearTimeout(timer); };
  }, [status, search, page, revision]);

  const wallet = data?.wallet;
  const transactions = data?.transactions || [];

  return (
    <main className="wallet-page">
      <header className="wallet-header">
        <div><p className="wallet-kicker">Vos ventes de photos</p><h1><FaWallet /> Mon portefeuille</h1><p>Consultez votre solde, vos ventes et vos retraits.</p></div>
        <div className="wallet-actions"><Link to="/payment-history">Historique des paiements</Link><button type="button" className="wallet-refresh" disabled={loading} onClick={() => setRevision(value => value + 1)}><FaSyncAlt /> Actualiser</button></div>
      </header>
      {error && <p className="wallet-error" role="alert">{error}</p>}
      <section className="wallet-summary" aria-label="Résumé du portefeuille">
        <article className="wallet-balance"><FaWallet className="wallet-summary-icon" aria-hidden="true" /><span>Solde disponible</span><strong>{wallet ? amount(wallet.balance) : '—'}</strong><small>Paiements terminés, après vos retraits</small></article>
        <article><FaClock className="wallet-summary-icon" aria-hidden="true" /><span>En attente</span><strong>{wallet ? amount(wallet.pending_amount) : '—'}</strong><small>Paiements non confirmés</small></article>
        <article><FaImages className="wallet-summary-icon" aria-hidden="true" /><span>Ventes</span><strong>{wallet?.transactions_count ?? '—'}</strong><small>{wallet?.completed_count ?? 0} terminées</small></article>
      </section>
      {wallet && <div className="wallet-outflows"><span>Retraits en cours : {amount(wallet.reserved_amount)}</span><span>Total retiré : {amount(wallet.withdrawn_amount)}</span></div>}
      <WalletWithdrawals wallet={wallet} settings={data?.withdrawal_settings} revision={revision} onBalanceChange={updateBalance} onWithdrawal={() => setRevision(value => value + 1)} />
      <section className="wallet-history" aria-busy={loading}>
        <div className="wallet-controls">
          <h2>Historique des ventes</h2>
          <label className="wallet-search"><FaSearch /><input aria-label="Rechercher une transaction" type="search" placeholder="Photo, événement, client ou référence" value={search} onChange={event => { setSearch(event.target.value); setPage(1); }} /></label>
          <select aria-label="Filtrer les transactions" value={status} onChange={event => { setStatus(event.target.value); setPage(1); }}><option value="">Tous les statuts</option>{Object.entries(STATUSES).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select>
        </div>
        {loading ? <p className="wallet-empty" role="status">Chargement du portefeuille…</p> : error ? <p className="wallet-empty">Actualisez pour réessayer.</p> : transactions.length === 0 ? <p className="wallet-empty">Aucune transaction trouvée. Vos ventes de photos apparaîtront ici.</p> : (
          <div className="wallet-transactions">
            {transactions.map(transaction => (
              <article className="wallet-transaction" key={transaction.id}>
                <div className="wallet-sale-icon" aria-hidden="true"><FaReceipt /></div>
                <div className="wallet-transaction-info"><strong>{transaction.photo_name}</strong><p>{transaction.event_name} · {transaction.buyer_name || 'Client'}</p><small>{transaction.transaction_id || `Vente ${transaction.purchase_id}`}</small><time dateTime={transaction.occurred_at}>{new Date(transaction.occurred_at).toLocaleString('fr-FR')}</time></div>
                <div className="wallet-transaction-value"><strong>{amount(transaction.amount)}</strong><span className={`wallet-status wallet-status-${transaction.status}`}>{STATUSES[transaction.status] || transaction.status}</span><small>{transaction.payment_method || 'Paiement photo'}</small></div>
              </article>
            ))}
          </div>
        )}
        {data?.meta && data.meta.last_page > 1 && <nav className="wallet-pagination" aria-label="Pages de transactions"><button type="button" disabled={loading || page <= 1} onClick={() => setPage(value => value - 1)}>Précédent</button><span>Page {data.meta.current_page} / {data.meta.last_page}</span><button type="button" disabled={loading || page >= data.meta.last_page} onClick={() => setPage(value => value + 1)}>Suivant</button></nav>}
      </section>
    </main>
  );
};

export default Wallet;
